import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import AsyncCreatableSelect from 'react-select/async-creatable';

export type TradeReferenceSuggestion = {
  name: string;
  email: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type TradeReferenceOption = TradeReferenceSuggestion & {
  label: string;
  value: string;
};

interface TradeReferenceCompanySelectProps {
  label?: string;
  isRequired?: boolean;
  errorText?: string;
  value: string;
  onTextChange: (text: string) => void;
  onSelectSuggestion: (suggestion: TradeReferenceSuggestion) => void;
  dataPrivate?: boolean;
}

const MIN_QUERY_LENGTH = 3;

const TradeReferenceCompanySelect: React.FC<TradeReferenceCompanySelectProps> = ({
  label,
  isRequired,
  errorText,
  value,
  onTextChange,
  onSelectSuggestion,
  dataPrivate,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<TradeReferenceOption[]>([]);
  const isFocusedRef = useRef(false);

  const debouncedLoadOptions = useCallback(
    debounce((inputVal: string, callback: (options: TradeReferenceOption[]) => void) => {
      const query = inputVal.trim().toUpperCase();
      if (query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        callback([]);
        return;
      }
      fetch(
        `${
          process.env.REACT_APP_API_URL
        }/creditApplications/searchTradeReferences?q=${encodeURIComponent(query)}`
      )
        .then((response) => response.json())
        .then((json: { data?: TradeReferenceSuggestion[] }) => {
          // Discard results that resolve after the field has already lost focus,
          // otherwise a late fetch reopens the suggestion menu over fields the
          // user has since moved on to.
          if (!isFocusedRef.current) {
            callback([]);
            return;
          }
          const options = (json?.data ?? []).map((suggestion) => {
            const addressLine = [
              suggestion.address,
              suggestion.city,
              suggestion.state,
              suggestion.zip,
            ]
              .filter(Boolean)
              .join(', ');
            return {
              ...suggestion,
              label: `${suggestion.name} — ${suggestion.email}${
                addressLine ? ` — ${addressLine}` : ''
              }`,
              value: suggestion.name,
            };
          });
          setSuggestions(options);
          callback(options);
        })
        .catch((error) => {
          console.error('Error fetching trade reference suggestions: ', error);
          setSuggestions([]);
          callback([]);
        });
    }, 400),
    []
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  // Fully controlled instead of relying on react-select's own focus/click-driven open
  // state, so the menu only ever appears once a fresh-enough query has real matches,
  // and never reappears on refocus alone (suggestions are cleared on blur).
  const menuIsOpen = value.trim().length >= MIN_QUERY_LENGTH && suggestions.length > 0;

  return (
    <div className="w-full flex flex-col">
      {(label || isRequired) && (
        <label className="text-xs mb-0.5 text-left whitespace-nowrap font-medium">
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </label>
      )}
      <div
        className="input-wrapper h-fit relative"
        style={{ borderColor: errorText ? '#F04438' : isFocused ? '#3B82F6' : '#E4EAF1' }}
        {...(dataPrivate ? { 'data-private': true } : {})}>
        <AsyncCreatableSelect
          placeholder=""
          isClearable={false}
          loadOptions={debouncedLoadOptions}
          inputValue={value}
          menuIsOpen={menuIsOpen}
          onFocus={() => {
            isFocusedRef.current = true;
            setIsFocused(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            setIsFocused(false);
            setSuggestions([]);
            debouncedLoadOptions.cancel();
          }}
          onChange={(newValue: any) => {
            if (!newValue) return;
            setSuggestions([]);
            debouncedLoadOptions.cancel();
            if (newValue.__isNew__) {
              onTextChange(newValue.value);
            } else {
              onSelectSuggestion({
                name: newValue.name,
                email: newValue.email,
                address: newValue.address,
                city: newValue.city,
                state: newValue.state,
                zip: newValue.zip,
              });
            }
          }}
          onInputChange={(inputVal: string, actionMeta: { action: string }) => {
            if (actionMeta.action === 'input-change') {
              setSuggestions([]);
              onTextChange(inputVal);
            }
          }}
          value={value ? { label: value, value } : null}
          styles={{
            control: (provided) => ({
              ...provided,
              minHeight: 'unset',
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              cursor: 'text',
            }),
            valueContainer: (provided) => ({ ...provided, padding: 0 }),
            input: (provided) => ({
              ...provided,
              margin: 0,
              padding: 0,
              fontSize: '0.8em',
              input: { opacity: '1 !important' },
            }),
            singleValue: (provided) => ({ ...provided, fontSize: '0.8em', margin: 0 }),
            placeholder: (provided) => ({ ...provided, fontSize: '0.8em', margin: 0 }),
            indicatorsContainer: (provided) => ({ ...provided, padding: 0 }),
            menu: (provided) => ({
              ...provided,
              zIndex: 50,
              fontSize: '0.8em',
              backgroundColor: '#fff',
              border: '1px solid #E4EAF1',
              boxShadow: '0 4px 12px rgba(16, 24, 40, 0.12)',
              overflow: 'hidden',
            }),
            menuList: (provided) => ({ ...provided, padding: 4 }),
            option: (provided, state) => ({
              ...provided,
              padding: '8px 12px',
              borderRadius: 6,
              backgroundColor: state.isFocused ? '#F5F7FA' : '#fff',
              color: 'inherit',
            }),
          }}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            DownChevron: () => null,
          }}
          formatCreateLabel={(inputVal: string) => <>Use "{inputVal}"</>}
          formatOptionLabel={(data: any, { context }) => {
            if (data.__isNew__ || context === 'value') return <>{data.label}</>;
            const option = data as TradeReferenceOption;
            const addressLine = [option.address, option.city, option.state, option.zip]
              .filter(Boolean)
              .join(', ');
            return (
              <div className="flex flex-col gap-0.5 py-0.5 leading-snug">
                <div className="flex items-baseline gap-1.5 truncate">
                  <span className="font-semibold text-gray-900 truncate">{option.name}</span>
                  {option.email && (
                    <span className="text-gray-500 truncate" style={{ fontSize: '0.85em' }}>
                      {option.email}
                    </span>
                  )}
                </div>
                {addressLine && (
                  <span className="text-gray-400 truncate" style={{ fontSize: '0.8em' }}>
                    {addressLine}
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>
      {errorText && <p className="error-text">{errorText}</p>}
    </div>
  );
};

export default TradeReferenceCompanySelect;
